"use client";

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import Link from "next/link";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import type { Dispute, DisputeVoteData } from "../types";
import { useT } from "@/hooks/useT";
import Tooltip from "@/app/components/Tooltip";
import { marketPageUrl } from "@/lib/source-url";
import {
  decodeOutcome,
  getOutcomeColor,
  getOutcomeBadgeClass,
  formatDecimalAmount,
  formatBond,
  timeAgo,
  STATE_COLORS,
  formatVolume,
} from "../utils";

/** Bar that animates from 0% to target width on mount */
function AnimatedBar({ pct, className, rounded = true }: { pct: number; className: string; rounded?: boolean }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setWidth(pct));
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  return (
    <div
      className={`h-full transition-all duration-700 ease-out ${rounded ? "rounded-[5px]" : ""} ${className}`}
      style={{ width: `${width}%` }}
    />
  );
}

interface DetailData extends Dispute {
  voteData: DisputeVoteData | null;
}

type SidebarTab = "result" | "details" | "lifecycle" | "summary" | "comments";

interface DiscordMessage {
  id: string;
  sender: string;
  senderPicture?: string;
  message: string;
  time: number;
  replies?: DiscordMessage[];
}

// Fallback GAT if subgraph doesn't provide minParticipationRequirement
const GAT_FALLBACK = 5_000_000;

// Use the shared portal-based Tooltip so long ES translations don't
// clip off the right edge of the modal — same fix applied across UMA.
function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip content={text} position="top" maxWidth={260}>
      <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-[5px] text-[9px] leading-none font-medium bg-gray-100 dark:bg-[#1A1A1A] text-gray-400 dark:text-gray-500 cursor-help ml-1">
        i
      </span>
    </Tooltip>
  );
}

interface DisputeSidebarProps {
  disputeId: string;
  onClose: () => void;
  currentPhase?: "commit" | "reveal";
}

/** Parse voting options from res_data in decoded ancillary data */
function parseVotingOptions(description: string | null): { label: string; value: string }[] {
  if (!description) return [];

  // Extract res_data section: "res_data: p1: 0, p2: 1, p3: 0.5. Where p1 corresponds to No, ..."
  const resMatch = description.match(/res_data:\s*(.+?)(?:,\s*initializer:|$)/s);
  if (!resMatch) return [];

  const resSection = resMatch[1];

  // Parse pN: value pairs
  const valuePairs = resSection.match(/p(\d+):\s*([\d.eE+-]+)/g);
  if (!valuePairs) return [];

  const values: Record<string, string> = {};
  for (const pair of valuePairs) {
    const m = pair.match(/p(\d+):\s*([\d.eE+-]+)/);
    if (m) values[`p${m[1]}`] = m[2];
  }

  // Parse labels from "Where p1 corresponds to No, p2 to Yes, ..."
  const labels: Record<string, string> = {};
  const whereMatch = resSection.match(/Where\s+(.+?)(?:\.\s*[A-Z]|\.\s*$|\.$|$)/s);
  if (whereMatch) {
    // Match "p1 corresponds to LABEL" or "p1 to LABEL" — stop at comma or period
    const labelParts = whereMatch[1].matchAll(/p(\d+)\s+(?:corresponds\s+)?to\s+([^,.]+)/gi);
    for (const lp of labelParts) {
      labels[`p${lp[1]}`] = lp[2].trim();
    }
  }

  // Build options: combine value + label
  const options: { label: string; value: string }[] = [];
  const sortedKeys = Object.keys(values).sort((a, b) => parseInt(a.slice(1)) - parseInt(b.slice(1)));
  for (const key of sortedKeys) {
    const val = values[key];
    const lbl = labels[key] || val;
    options.push({ label: `${lbl} (${key}: ${val})`, value: val });
  }

  // Always add "Early Request" if not already present (voters can always vote this)
  const EARLY_REQUEST_VAL = "-57896044618658097711785492504343953926634992332820282019728792003956564819968";
  const hasEarly = options.some(o => o.value === EARLY_REQUEST_VAL || o.label.toLowerCase().includes("early"));
  if (!hasEarly && options.length > 0) {
    // Module-level helper — `t` isn't in scope here; the consumer
    // translates this label at render time when iterating options.
    options.push({ label: "Early Request / Too Early", value: EARLY_REQUEST_VAL });
  }

  return options;
}

/** Render Discord-flavored markdown: **bold**, *italic*, __underline__, ~~strikethrough~~, `code`, URLs */
function renderDiscordMarkdown(text: string): React.ReactNode {
  // Combined regex: code blocks, inline code, bold+italic, bold, underline, italic, strikethrough, URLs
  const TOKEN_RE = /```([\s\S]*?)```|`([^`]+)`|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|_(.+?)_|~~(.+?)~~|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s),]+)/g;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = TOKEN_RE.exec(text)) !== null) {
    // Push plain text before this match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      // ```code block```
      nodes.push(
        <code key={key++} className="block text-[10px] bg-gray-100 dark:bg-[#18181D] rounded px-2 py-1.5 my-1 whitespace-pre-wrap font-mono text-gray-800 dark:text-gray-300">
          {match[1].trim()}
        </code>
      );
    } else if (match[2] !== undefined) {
      // `inline code`
      nodes.push(
        <code key={key++} className="text-[10px] bg-gray-100 dark:bg-[#18181D] rounded px-1 py-0.5 font-mono text-gray-800 dark:text-gray-300">
          {match[2]}
        </code>
      );
    } else if (match[3] !== undefined) {
      // ***bold italic***
      nodes.push(<strong key={key++}><em>{match[3]}</em></strong>);
    } else if (match[4] !== undefined) {
      // **bold**
      nodes.push(<strong key={key++} className="font-semibold text-gray-900 dark:text-white">{match[4]}</strong>);
    } else if (match[5] !== undefined) {
      // __underline__
      nodes.push(<span key={key++} className="underline">{match[5]}</span>);
    } else if (match[6] !== undefined) {
      // *italic*
      nodes.push(<em key={key++}>{match[6]}</em>);
    } else if (match[7] !== undefined) {
      // _italic_
      nodes.push(<em key={key++}>{match[7]}</em>);
    } else if (match[8] !== undefined) {
      // ~~strikethrough~~
      nodes.push(<span key={key++} className="line-through text-gray-400">{match[8]}</span>);
    } else if (match[9] !== undefined && match[10] !== undefined) {
      // [text](url) markdown link
      nodes.push(
        <a
          key={key++}
          href={match[10]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {match[9]}
        </a>
      );
    } else if (match[11] !== undefined) {
      // bare URL
      nodes.push(
        <a
          key={key++}
          href={match[11]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline break-all"
        >
          {match[11]}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

/** Extract the description text from decoded ancillary data */
function parseDescription(raw: string | null): string | null {
  if (!raw) return null;
  const match = raw.match(/description:\s*(.+?)(?:,\s*res_data:|$)/s);
  return match ? match[1].trim() : null;
}

/** Extract "Additional context | Polymarket" section from raw ancillary data description */
function parseAdditionalContext(raw: string | null): string | null {
  if (!raw) return null;
  // Look for "Additional context" pattern (case-insensitive)
  const match = raw.match(/additional\s+(?:context|information)\s*(?:\|\s*\w+)?:?\s*(.+?)(?:,\s*res_data:|,\s*uma_bond:|$)/si);
  return match ? match[1].trim() : null;
}

/** Format unix timestamp to UTC string */
function formatTimestamp(ts: number): string {
  return new Date(ts * 1000).toUTCString();
}

/** Map Tailwind outcome bg class to hex color for recharts */
function outcomeHex(price: string): string {
  const cls = getOutcomeColor(price);
  if (cls.includes("green")) return "#22c55e";
  if (cls.includes("red")) return "#ef4444";
  if (cls.includes("blue")) return "#3b82f6";
  if (cls.includes("purple")) return "#a855f7";
  return "#6b7280";
}

/** Parse subgraph BigDecimal string (already in UMA tokens, not wei) */
function tokensToNum(raw: string): number {
  return parseFloat(raw || "0") || 0;
}

/** Format raw token number (already divided by 1e18) for display */
function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(4);
}


export default function DisputeSidebar({ disputeId, onClose, currentPhase }: DisputeSidebarProps) {
  const t = useT();
  const { isLoggedIn: authenticated, isReady: authReady, login } = useAuth();
  const [dispute, setDispute] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<SidebarTab>("result");
  const [descExpanded, setDescExpanded] = useState(false);
  const [comments, setComments] = useState<DiscordMessage[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsFetched, setCommentsFetched] = useState(false);
  const [closing, setClosing] = useState(false);
  const blurClass = authReady && !authenticated ? "blur-[6px] select-none pointer-events-none" : "";

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 200); // Match slide-out-right duration
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    setTab(currentPhase === "commit" ? "details" : "result");
    setDescExpanded(false);
    setComments([]);
    setCommentsFetched(false);
    setClosing(false);
    fetch(`/api/uma/disputes/${disputeId}`)
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data) setDispute(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [disputeId]);

  const fetchComments = useCallback(() => {
    if (commentsFetched || commentsLoading) return;
    setCommentsLoading(true);
    fetch(`/api/uma/disputes/${disputeId}/comments`)
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data?.thread) setComments(res.data.thread);
      })
      .catch(() => {})
      .finally(() => {
        setCommentsLoading(false);
        setCommentsFetched(true);
      });
  }, [disputeId, commentsFetched, commentsLoading]);

  useEffect(() => {
    if (tab === "comments" && !commentsFetched) fetchComments();
  }, [tab, commentsFetched, fetchComments]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleClose]);

  const vd = dispute?.voteData;
  const totalVoteTokens = vd?.groups.reduce(
    (sum, g) => sum + parseFloat(g.totalVoteAmount || "0"),
    0
  ) || 0;

  // Hide "Result" tab during commit phase for active disputes (no revealed data yet)
  const isCommitActive = currentPhase === "commit" && dispute?.dvmStatus === "active";

  // Lifecycle steps for the resolution timeline
  const lifecycleSteps = useMemo(() => {
    if (!dispute) return [];
    const d = dispute;
    const isProposed = d.state === "Proposed";
    const isDisputed = d.state === "Disputed";
    const isResolved = d.state === "Resolved";
    const isSettled = d.state === "Settled";
    const isDvmActive = d.dvmStatus === "active";
    const isCommit = currentPhase === "commit";
    type S = "completed" | "active" | "upcoming";
    return [
      {
        id: "proposal",
        label: t("uma.dsb.lc.proposal"),
        timeEstimate: t("uma.dsb.lc.proposalWindow"),
        description: t("uma.dsb.lc.proposalDesc"),
        status: (isDisputed || isResolved || isSettled ? "completed" : isProposed ? "active" : "upcoming") as S,
        timestamp: d.proposalTime,
        subSteps: null as { label: string; status: S }[] | null,
      },
      {
        id: "dispute",
        label: t("uma.dsb.lc.dispute"),
        timeEstimate: null as string | null,
        description: t("uma.dsb.lc.disputeDesc"),
        status: ((isResolved || isSettled) ? "completed" : isDisputed ? "active" : "upcoming") as S,
        timestamp: d.disputeTime,
        subSteps: null as { label: string; status: S }[] | null,
      },
      {
        id: "dvm-vote",
        label: t("uma.dsb.lc.dvmVote"),
        timeEstimate: t("uma.dsb.lc.dvmTime") as string | null,
        description: t("uma.dsb.lc.dvmDesc"),
        status: (
          (isResolved || isSettled) ? "completed" :
          (isDisputed && isDvmActive) ? "active" :
          "upcoming"
        ) as S,
        timestamp: d.dvmTime || null,
        subSteps: (isDisputed && isDvmActive) ? [
          { label: t("uma.dsb.lc.commitPhase"), status: (isCommit ? "active" : "completed") as S },
          { label: t("uma.dsb.lc.revealPhase"), status: (!isCommit ? "active" : "upcoming") as S },
        ] : null,
      },
      {
        id: "resolution",
        label: t("uma.dsb.lc.resolution"),
        timeEstimate: null as string | null,
        description: t("uma.dsb.lc.resolutionDesc"),
        status: (isSettled ? "completed" : isResolved ? "active" : "upcoming") as S,
        timestamp: null as number | null,
        subSteps: null as { label: string; status: S }[] | null,
      },
      {
        id: "settlement",
        label: t("uma.dsb.lc.settlement"),
        timeEstimate: null as string | null,
        description: t("uma.dsb.lc.settlementDesc"),
        status: (isSettled ? "completed" : "upcoming") as S,
        timestamp: d.settlementTime,
        subSteps: null as { label: string; status: S }[] | null,
      },
    ];
  }, [dispute, currentPhase, t]);

  const tabs: { key: SidebarTab; label: string }[] = [
    ...(!isCommitActive ? [{ key: "result" as SidebarTab, label: t("uma.dsb.tab.result") }] : []),
    { key: "details", label: t("uma.dsb.tab.details") },
    { key: "lifecycle", label: t("uma.dsb.tab.lifecycle") },
    ...(dispute?.aiSummary ? [{ key: "summary" as SidebarTab, label: t("uma.dsb.tab.summary") }] : []),
    { key: "comments", label: t("uma.dsb.tab.comments") },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 dark:bg-black/50 z-40 transition-opacity ${closing ? "opacity-0" : ""}`}
        onClick={handleClose}
      />

      {/* Sidebar */}
      <div className={`fixed top-14 right-0 h-[calc(100%-3.5rem)] w-full max-w-lg bg-white dark:bg-[#0f0f12] border-l border-gray-200 dark:border-[#1A1A1A] z-50 overflow-y-auto overflow-x-hidden pb-16 ${closing ? "animate-slide-out-right" : "animate-slide-in-right"}`}>
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#0f0f12] border-b border-gray-100 dark:border-[#1A1A1A] px-5 py-4 flex items-center justify-between z-10">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {currentPhase === "commit" && dispute?.dvmStatus === "active" ? t("uma.dsb.commitDetail") : t("uma.dsb.disputeDetail")}
          </span>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors p-1.5 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#1A1A1A]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="p-5 space-y-4">
            <div className="h-6 w-20 rounded bg-gray-100 dark:bg-[#18181D] animate-pulse" />
            <div className="h-16 rounded-[5px] bg-gray-100 dark:bg-[#18181D] animate-pulse" />
            <div className="h-40 rounded-[5px] bg-gray-100 dark:bg-[#18181D] animate-pulse" />
            <div className="h-32 rounded-[5px] bg-gray-100 dark:bg-[#18181D] animate-pulse" />
          </div>
        ) : !dispute ? (
          <div className="p-5 text-center py-20">
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t("uma.dsb.notFound")}</p>
          </div>
        ) : (
          <div className="p-4 sm:p-5 space-y-3 sm:space-y-5">
            {/* Title + badge */}
            <div className="flex items-center gap-3">
              {dispute.pmImage && (
                <div className="relative w-[55px] h-[55px] rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#111] ring-1 ring-black/5 dark:ring-white/5 flex-shrink-0">
                  <img src={dispute.pmImage} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {dispute.pmSlug ? (
                  <Link
                    href={marketPageUrl({
                      marketSlug: dispute.pmSlug,
                      eventSlug: dispute.polymarketEventSlug,
                      source: 'polymarket',
                    })}
                    prefetch={false}
                    className="font-satoshi font-black text-[13px] text-gray-900 dark:text-white hover:underline leading-snug"
                  >
                    {dispute.resolvedTitle || dispute.pmTitle || dispute.question || t("uma.dsb.unknownDispute")}
                  </Link>
                ) : (
                  <p className="font-satoshi font-black text-[13px] text-gray-900 dark:text-white leading-snug">
                    {dispute.resolvedTitle || dispute.pmTitle || dispute.question || t("uma.dsb.unknownDispute")}
                  </p>
                )}
              </div>
            </div>

            {/* Tabs — pills style */}
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-x-auto scrollbar-hide">
              {tabs.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex-1 text-xs font-medium px-3 py-2 rounded-[5px] transition-colors whitespace-nowrap flex-shrink-0 ${
                    tab === t.key
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* ═══ RESULT TAB ═══ */}
            {tab === "result" && (
              <div className="relative">
              {authReady && !authenticated && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <button
                    onClick={() => login()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-[5px] text-xs font-bold bg-[#CCFF00] text-gray-900 hover:bg-[#b8e600] transition-colors shadow-lg"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {t("uma.dsb.signIn")}
                  </button>
                </div>
              )}
              <div className={`space-y-5 ${blurClass}`}>
                {vd && vd.groups.length > 0 ? (
                  <>
                    {/* Vote breakdown: donut + labels */}
                    {(() => {
                      const sorted = [...vd.groups].sort(
                        (a, b) => parseFloat(b.totalVoteAmount || "0") - parseFloat(a.totalVoteAmount || "0")
                      );
                      const donutData = sorted.map(g => ({
                        name: decodeOutcome(g.price),
                        value: parseFloat(g.totalVoteAmount || "0"),
                        color: outcomeHex(g.price),
                      }));
                      const winner = sorted.find(g => g.won);

                      return (
                        <div className="flex items-center gap-4 rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                          {/* Donut */}
                          <div className="w-36 h-36 flex-shrink-0 relative">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={donutData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={48}
                                  outerRadius={64}
                                  dataKey="value"
                                  stroke="none"
                                >
                                  {donutData.map((entry, i) => (
                                    <Cell key={i} fill={entry.color} />
                                  ))}
                                </Pie>
                              </PieChart>
                            </ResponsiveContainer>
                            {/* Center label */}
                            {winner && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-lg leading-none">👑</span>
                                <span className="text-xs font-bold text-amber-600 dark:text-amber-400 leading-tight mt-0.5">
                                  {decodeOutcome(winner.price)}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Labels */}
                          <div className="flex-1 space-y-2">
                            {sorted.map((group, i) => {
                              const groupTokens = parseFloat(group.totalVoteAmount || "0");
                              const pct = totalVoteTokens > 0 ? (groupTokens / totalVoteTokens) * 100 : 0;
                              const isWinner = group.won || (pct >= 99 && groupTokens > 0);
                              return (
                                <div key={i} className={`flex items-center justify-between ${isWinner ? "px-2 py-1 rounded-[5px] bg-amber-50 dark:bg-amber-900/20" : ""}`}>
                                  <div className="flex items-center gap-2">
                                    {isWinner
                                      ? <span className="text-sm">👑</span>
                                      : <div className="w-2.5 h-2.5 rounded-[5px] flex-shrink-0" style={{ backgroundColor: outcomeHex(group.price) }} />
                                    }
                                    <span className={`text-xs font-medium ${getOutcomeBadgeClass(group.price)} px-1.5 py-0.5 rounded`}>
                                      {decodeOutcome(group.price)}
                                    </span>
                                  </div>
                                  <span className={`text-xs font-medium tabular-nums ${isWinner ? "font-bold text-amber-600 dark:text-amber-400" : "text-gray-900 dark:text-white"}`}>
                                    {pct.toFixed(1)}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Participation section */}
                    <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                      <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-3">
                        {t("uma.dsb.participation")}
                        <InfoTip text={t("uma.dsb.tip.participation")} />
                      </h3>
                      <div className="space-y-3">
                        {/* Quorum bar */}
                        {(() => {
                          const revealed = tokensToNum(vd.totalVotesRevealed);
                          const gatValue = vd.minParticipationRequirement
                            ? tokensToNum(vd.minParticipationRequirement)
                            : GAT_FALLBACK;
                          const quorumPct = gatValue > 0 ? Math.min((revealed / gatValue) * 100, 100) : 0;
                          const quorumMet = revealed >= gatValue;
                          return (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] text-gray-600 dark:text-gray-300">
                                    {t("uma.dsb.quorum", { pct: quorumPct.toFixed(0) })}
                                    <InfoTip text={t("uma.dsb.tip.quorum")} />
                                  </span>
                                  {quorumMet ? (
                                    <span className="text-green-500 text-[11px]">&#10003;</span>
                                  ) : (
                                    <span className="text-red-500 text-[11px]">&#10007;</span>
                                  )}
                                </div>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                  {fmtNum(revealed)} / {fmtNum(gatValue)}
                                </span>
                              </div>
                              <div className="w-full h-2 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] overflow-hidden">
                                <AnimatedBar pct={Math.max(quorumPct, 1)} className={quorumMet ? "bg-green-500" : "bg-red-400"} />
                              </div>
                            </div>
                          );
                        })()}

                        {/* Consensus bar (SPAT) — leading tokens vs minAgreementRequirement */}
                        {(() => {
                          if (totalVoteTokens === 0) return null;
                          const winner = vd.groups.find(g => g.won);
                          const sorted = [...vd.groups].sort(
                            (a, b) => parseFloat(b.totalVoteAmount || "0") - parseFloat(a.totalVoteAmount || "0")
                          );
                          const leading = winner || sorted[0];
                          if (!leading) return null;
                          const leadingTokens = parseFloat(leading.totalVoteAmount || "0");
                          const spatValue = vd.minAgreementRequirement
                            ? tokensToNum(vd.minAgreementRequirement)
                            : totalVoteTokens * 0.65; // fallback ~65%
                          const spatPct = spatValue > 0 ? Math.min((leadingTokens / spatValue) * 100, 100) : 0;
                          const isConfirmed = !!winner || leadingTokens >= spatValue;
                          return (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] text-gray-600 dark:text-gray-300">
                                    {t("uma.dsb.consensus", { pct: spatPct.toFixed(0) })}
                                    <InfoTip text={t("uma.dsb.tip.consensus")} />
                                  </span>
                                  {isConfirmed ? (
                                    <span className="text-green-500 text-[11px]">&#10003;</span>
                                  ) : (
                                    <span className="text-[11px] text-gray-400">{t("uma.dsb.leading", { outcome: decodeOutcome(leading.price) })}</span>
                                  )}
                                </div>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                  {fmtNum(leadingTokens)} / {fmtNum(spatValue)}
                                </span>
                              </div>
                              <div className="w-full h-2 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] overflow-hidden">
                                <AnimatedBar pct={Math.max(spatPct, 1)} className={isConfirmed ? "bg-green-500" : "bg-blue-500"} />
                              </div>
                            </div>
                          );
                        })()}

                        {/* Stats rows */}
                        <div className="space-y-2 pt-1">
                          {[
                            {
                              label: t("uma.dsb.stat.commitAddrs"),
                              value: ((vd.revealedVotes?.length || 0) + (vd.unrevealedVoters?.length || 0)).toString(),
                              tip: t("uma.dsb.tip.commitAddrs"),
                            },
                            {
                              label: t("uma.dsb.stat.revealAddrs"),
                              value: vd.votersAmount.toString(),
                              tip: t("uma.dsb.tip.revealAddrs"),
                            },
                            {
                              label: t("uma.dsb.stat.tokensCommitted"),
                              value: vd.totalTokensCommitted && vd.totalTokensCommitted !== "0"
                                ? fmtNum(tokensToNum(vd.totalTokensCommitted))
                                : "—",
                              tip: t("uma.dsb.tip.tokensCommitted"),
                            },
                            {
                              label: t("uma.dsb.stat.tokensRevealed"),
                              value: vd.totalVotesRevealed && vd.totalVotesRevealed !== "0"
                                ? `${vd.participationPercentage ? `${parseFloat(vd.participationPercentage).toFixed(1)}%` : ""} ${fmtNum(tokensToNum(vd.totalVotesRevealed))}`.trim()
                                : "—",
                              highlight: true,
                              tip: t("uma.dsb.tip.tokensRevealed"),
                            },
                            ...(vd.rollCount > 0 ? [{
                              label: t("uma.dsb.stat.voteRolls"),
                              value: t(vd.rollCount > 1 ? "uma.dsb.rolls" : "uma.dsb.roll", { count: vd.rollCount }),
                              highlight: false,
                              tip: t("uma.dsb.tip.voteRolls"),
                            }] : []),
                          ].map(row => (
                            <div key={row.label} className="flex items-center justify-between py-1">
                              <span className="text-[11px] text-gray-600 dark:text-gray-400">
                                {row.label}
                                {row.tip && <InfoTip text={row.tip} />}
                              </span>
                              <span className={`text-[11px] font-medium tabular-nums ${row.highlight ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-white"}`}>
                                {row.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Slashing Impact (only for resolved disputes) */}
                    {vd.slashingTracker && parseFloat(vd.slashingTracker.totalSlashed) > 0 && (
                      <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                        <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-3">
                          {t("uma.dsb.slashing")}
                          <InfoTip text={t("uma.dsb.tip.slashing")} />
                        </h3>
                        <div className="space-y-2">
                          {[
                            { label: t("uma.dsb.stat.totalSlashed"), value: `${fmtNum(tokensToNum(vd.slashingTracker.totalSlashed))} UMA`, tip: t("uma.dsb.tip.totalSlashed"), color: "text-red-600 dark:text-red-400" },
                            { label: t("uma.dsb.stat.wrongVoteSlash"), value: `${(parseFloat(vd.slashingTracker.wrongVoteSlashPerToken) * 100).toFixed(4)}%`, tip: t("uma.dsb.tip.wrongVoteSlash"), color: "text-red-600 dark:text-red-400" },
                            { label: t("uma.dsb.stat.noVoteSlash"), value: `${(parseFloat(vd.slashingTracker.noVoteSlashPerToken) * 100).toFixed(4)}%`, tip: t("uma.dsb.tip.noVoteSlash"), color: "text-red-600 dark:text-red-400" },
                            { label: t("uma.dsb.stat.totalCorrectVotes"), value: `${fmtNum(tokensToNum(vd.slashingTracker.totalCorrectVotes))} UMA`, tip: t("uma.dsb.tip.totalCorrect"), color: "text-green-600 dark:text-green-400" },
                          ].map(row => (
                            <div key={row.label} className="flex items-center justify-between py-1">
                              <span className="text-[11px] text-gray-600 dark:text-gray-400">
                                {row.label}
                                {row.tip && <InfoTip text={row.tip} />}
                              </span>
                              <span className={`text-[11px] font-medium tabular-nums ${row.color}`}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : dispute.state === "Disputed" ? (
                  <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] p-4 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("uma.dsb.noVoteData.disputed")}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] p-4 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("uma.dsb.noVoteData")}
                    </p>
                  </div>
                )}
              </div>
              </div>
            )}

            {/* ═══ DETAILS TAB ═══ */}
            {tab === "details" && (
              <div className="space-y-5">
                {/* Type pills — scrollable row */}
                <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                  <span className="text-[11px] font-medium px-2.5 py-1 rounded-[5px] bg-gray-100 dark:bg-[#18181D] text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                    <svg viewBox="0 0 84 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 flex-shrink-0"><circle cx="42" cy="42" r="42" fill="#EFEFEF" className="dark:fill-[#2a2a2a]" /><g clipPath="url(#pol_a)"><path d="M55.09 33.398c-.92-.526-2.102-.526-3.153 0l-7.355 4.334-4.991 2.758-7.224 4.334c-.92.526-2.101.526-3.152 0l-5.648-3.415c-.92-.525-1.576-1.576-1.576-2.758v-6.567c0-1.05.525-2.101 1.576-2.758l5.648-3.284c.92-.525 2.101-.525 3.152 0l5.648 3.415c.92.526 1.576 1.576 1.576 2.758v4.335l4.991-2.89v-4.465c0-1.051-.525-2.102-1.576-2.759l-10.508-6.173c-.919-.525-2.101-.525-3.152 0l-10.77 6.305c-1.05.525-1.576 1.576-1.576 2.627V41.54c0 1.05.525 2.101 1.576 2.758l10.639 6.173c.92.526 2.101.526 3.152 0l7.224-4.203 4.991-2.89 7.224-4.202c.92-.526 2.102-.526 3.152 0l5.648 3.283c.92.526 1.576 1.576 1.576 2.758v6.568c0 1.05-.525 2.101-1.576 2.758l-5.516 3.283c-.92.526-2.102.526-3.153 0l-5.647-3.283c-.92-.526-1.577-1.576-1.577-2.758v-4.203l-4.99 2.89v4.334c0 1.05.525 2.1 1.575 2.758l10.64 6.173c.919.525 2.1.525 3.152 0l10.638-6.173c.92-.526 1.577-1.576 1.577-2.758V42.328c0-1.05-.526-2.102-1.577-2.758L55.09 33.398Z" fill="#8247E5" /></g><defs><clipPath id="pol_a"><path fill="#fff" transform="translate(17 20)" d="M0 0h50.436v44H0z" /></clipPath></defs></svg>
                    {dispute.originatingChainId === 1 ? "Ethereum" : "Polygon"}
                  </span>
                  <span className="text-[11px] font-medium px-2.5 py-1 rounded-[5px] bg-gray-100 dark:bg-[#18181D] text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
                    <svg viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-2 flex-shrink-0"><path fillRule="evenodd" clipRule="evenodd" d="M18 1H6a5 5 0 0 0 0 10h12a5 5 0 0 0 0-10ZM6 0a6 6 0 1 0 0 12h12a6 6 0 0 0 0-12H6Zm1 8.999a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 1a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13-4a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm1 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" fill="currentColor" /></svg>
                    {dispute.originatingOracleType || "Optimistic Oracle v2"}
                  </span>
                  <span className="text-[11px] font-medium px-2.5 py-1 rounded-[5px] bg-gray-100 dark:bg-[#18181D] text-gray-700 dark:text-gray-300 whitespace-nowrap flex-shrink-0">
                    {dispute.identifier || "YES_OR_NO_QUERY"}
                  </span>
                  {dispute.ooRequestUrl && (
                    <a
                      href={dispute.ooRequestUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-medium px-2.5 py-1 rounded-[5px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      {t("uma.dsb.viewOnOracle")}
                    </a>
                  )}
                </div>

                {/* Description */}
                {(() => {
                  const desc = parseDescription(dispute.description);
                  if (!desc) return null;
                  const isLong = desc.length > 300;
                  const pmLink = dispute.polymarketEventSlug || null;
                  return (
                    <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-medium text-gray-900 dark:text-white">{t("uma.dsb.description")}</h3>
                        {pmLink && (
                          <a
                            href={`https://polymarket.com/event/${pmLink}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <img src="/polymarket-logo-blue.png" alt="Polymarket" className="w-3.5 h-3.5 flex-shrink-0 object-contain" />
                            {t("uma.dsb.seeOnPolymarket")}
                          </a>
                        )}
                      </div>
                      <div
                        className={`text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line break-words overflow-hidden ${!descExpanded && isLong ? "line-clamp-4" : ""}`}
                      >
                        {renderDiscordMarkdown(desc)}
                      </div>
                      {isLong && (
                        <button
                          onClick={() => setDescExpanded(!descExpanded)}
                          className="flex items-center gap-1 mt-1.5 text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {descExpanded ? t("uma.dsb.showLess") : t("uma.dsb.readMore")}
                          <svg
                            className={`w-3 h-3 transition-transform ${descExpanded ? "rotate-180" : ""}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Additional Context */}
                {(() => {
                  const ctx = parseAdditionalContext(dispute.description);
                  if (!ctx) return null;
                  return (
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-2">{t("uma.dsb.additionalContext")}</h3>
                      <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line break-words overflow-hidden rounded-[5px] bg-gray-50 dark:bg-[#18181D] p-3">
                        {renderDiscordMarkdown(ctx)}
                      </div>
                    </div>
                  );
                })()}

                {/* Voting Options */}
                {(() => {
                  const options = parseVotingOptions(dispute.description);
                  if (options.length === 0) return null;
                  return (
                    <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                      <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-2">{t("uma.dsb.votingOptions")}</h3>
                      <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                        {options.map((opt, i) => (
                          <div key={i} className="px-3 py-2">
                            <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                              {opt.label === "Early Request / Too Early" ? t("uma.dsb.outcome.earlyRequest") : opt.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Timestamp */}
                {(dispute.dvmTime || dispute.requestTime) && (
                  <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                    <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-2">{t("uma.dsb.timestamp")}</h3>
                    <div className="space-y-1 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">UTC</span>
                        <span className="text-gray-900 dark:text-white">{formatTimestamp((dispute.dvmTime || dispute.requestTime)!)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">UNIX</span>
                        <span className="text-gray-900 dark:text-white font-mono">{dispute.dvmTime || dispute.requestTime}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Links — same as vote.uma.xyz */}
                <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] p-4">
                  <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-2">{t("uma.dsb.links")}</h3>
                  <div className="space-y-1.5">
                    {dispute.pmSlug && (
                      <Link
                        href={marketPageUrl({
                          marketSlug: dispute.pmSlug,
                          eventSlug: dispute.polymarketEventSlug,
                          source: 'polymarket',
                        })}
                        prefetch={false}
                        className="block text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {t("uma.dsb.seeOnFrenflow")}
                      </Link>
                    )}
                    {dispute.ooRequestUrl && (
                      <a
                        href={dispute.ooRequestUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {t("uma.dsb.oracleRequest")}
                      </a>
                    )}
                    <a
                      href="https://github.com/UMAprotocol/UMIPs/blob/master/UMIPs/umip-107.md"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      UMIP-107
                    </a>
                  </div>
                </div>

              </div>
            )}

            {/* ═══ LIFECYCLE TAB ═══ */}
            {tab === "lifecycle" && (
              <div className="space-y-6">
                {/* Resolution Timeline */}
                <div>
                  <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-4">
                    {t("uma.dsb.lc.title")}
                  </h3>
                  <div className="relative pl-7">
                    {/* Connecting line */}
                    <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-gray-200 dark:bg-[#1A1A1A]" />

                    {lifecycleSteps.map((step, i) => (
                      <div key={step.id} className={`relative flex gap-3 ${i < lifecycleSteps.length - 1 ? "pb-5" : ""}`}>
                        {/* Circle */}
                        <div className={`absolute -left-7 w-[16px] h-[16px] rounded-[5px] border-2 flex items-center justify-center flex-shrink-0 z-10 ${
                          step.status === "completed"
                            ? "bg-green-500 border-green-500"
                            : step.status === "active"
                            ? "bg-blue-500 border-blue-500 ring-4 ring-blue-500/20"
                            : "bg-white dark:bg-[#0f0f12] border-gray-300 dark:border-gray-600"
                        }`}>
                          {step.status === "completed" && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {step.status === "active" && (
                            <div className="w-1.5 h-1.5 rounded-[5px] bg-white" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 -mt-0.5">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-xs font-medium ${
                              step.status === "active" ? "text-blue-600 dark:text-blue-400" :
                              step.status === "completed" ? "text-gray-900 dark:text-white" :
                              "text-gray-400 dark:text-gray-500"
                            }`}>
                              {step.label}
                            </span>
                            {step.timeEstimate && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {step.timeEstimate}
                              </span>
                            )}
                            {step.status === "active" && (
                              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-[5px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                {t("uma.dsb.lc.current")}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                            {step.description}
                          </p>
                          {step.timestamp && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 block">
                              {new Date(step.timestamp * 1000).toLocaleString()}
                            </span>
                          )}
                          {/* Sub-steps for DVM voting phases */}
                          {step.subSteps && (
                            <div className="mt-2 space-y-1.5">
                              {step.subSteps.map(sub => (
                                <div key={sub.label} className="flex items-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-[5px] flex-shrink-0 ${
                                    sub.status === "completed" ? "bg-green-500" :
                                    sub.status === "active" ? "bg-blue-500" :
                                    "bg-gray-300 dark:bg-gray-600"
                                  }`} />
                                  <span className={`text-[11px] ${
                                    sub.status === "active" ? "text-blue-600 dark:text-blue-400 font-medium" :
                                    sub.status === "completed" ? "text-gray-600 dark:text-gray-300" :
                                    "text-gray-400 dark:text-gray-500"
                                  }`}>
                                    {sub.label}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bond & Economics */}
                {dispute.bond && formatBond(dispute.bond) && (
                  <div>
                    <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-3">
                      {t("uma.dsb.bond.title")}
                    </h3>
                    <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] p-3 space-y-2">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-500 dark:text-gray-400">{t("uma.dsb.bond.proposer")}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatBond(dispute.bond)} USDC.e</span>
                      </div>
                      {dispute.disputer && (
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-500 dark:text-gray-400">{t("uma.dsb.bond.disputer")}</span>
                          <span className="font-medium text-gray-900 dark:text-white">{formatBond(dispute.bond)} USDC.e</span>
                        </div>
                      )}
                      {dispute.disputer && (
                        <div className="flex items-center justify-between text-[11px] pt-1 border-t border-gray-200 dark:border-[#252525]">
                          <span className="text-gray-500 dark:text-gray-400">{t("uma.dsb.bond.totalAtStake")}</span>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            ${(Number(BigInt(dispute.bond || "0")) / 1e6 * 2).toLocaleString(undefined, { maximumFractionDigits: 0 })} USDC.e
                          </span>
                        </div>
                      )}

                      {/* Distribution rules */}
                      <div className="pt-2 mt-1 border-t border-gray-200 dark:border-[#252525] space-y-1.5">
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                          <span className="text-green-600 dark:text-green-400 font-medium">{t("uma.dsb.bond.winner")}</span> {t("uma.dsb.bond.winnerDesc")}
                        </p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                          <span className="text-red-600 dark:text-red-400 font-medium">{t("uma.dsb.bond.loser")}</span> {t("uma.dsb.bond.loserDesc")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* What Outcomes Mean for Traders */}
                <div>
                  <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-3">
                    {t("uma.dsb.outcomes.title")}
                  </h3>
                  <div className="rounded-[5px] bg-gray-50 dark:bg-[#18181D] divide-y divide-gray-100 dark:divide-[#252525]">
                    {[
                      { outcome: t("uma.dsb.outcome.yes"), color: "text-green-600 dark:text-green-400", bg: "bg-green-500", desc: t("uma.dsb.outcomes.yesDesc") },
                      { outcome: t("uma.dsb.outcome.no"), color: "text-red-600 dark:text-red-400", bg: "bg-red-500", desc: t("uma.dsb.outcomes.noDesc") },
                      { outcome: t("uma.dsb.outcome.tooEarly"), color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-500", desc: t("uma.dsb.outcomes.tooEarlyDesc") },
                      { outcome: t("uma.dsb.outcome.unknown"), color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500", desc: t("uma.dsb.outcomes.unknownDesc") },
                    ].map(item => (
                      <div key={item.outcome} className="px-3 py-2.5 flex gap-3">
                        <div className={`w-2 h-2 rounded-[5px] ${item.bg} mt-1 flex-shrink-0`} />
                        <div>
                          <span className={`text-[11px] font-medium ${item.color}`}>{item.outcome}</span>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* How DVM Voting Works */}
                <div>
                  <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-3">
                    {t("uma.dsb.dvm.title")}
                  </h3>
                  <div className="space-y-2.5 text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
                    <div className="flex gap-2.5">
                      <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px] mt-px flex-shrink-0">01</span>
                      <p><span className="text-gray-900 dark:text-white font-medium">{t("uma.dsb.dvm.row1Title")}</span> &mdash; {t("uma.dsb.dvm.row1Desc")}</p>
                    </div>
                    <div className="flex gap-2.5">
                      <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px] mt-px flex-shrink-0">02</span>
                      <p><span className="text-gray-900 dark:text-white font-medium">{t("uma.dsb.dvm.row2Title")}</span> &mdash; {t("uma.dsb.dvm.row2Desc")}</p>
                    </div>
                    <div className="flex gap-2.5">
                      <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px] mt-px flex-shrink-0">03</span>
                      <p><span className="text-gray-900 dark:text-white font-medium">{t("uma.dsb.dvm.row3Title")}</span> &mdash; {t("uma.dsb.dvm.row3Desc")}</p>
                    </div>
                    <div className="flex gap-2.5">
                      <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px] mt-px flex-shrink-0">04</span>
                      <p><span className="text-gray-900 dark:text-white font-medium">{t("uma.dsb.dvm.row4Title")}</span> &mdash; {t("uma.dsb.dvm.row4Desc")}</p>
                    </div>
                    <div className="flex gap-2.5">
                      <span className="text-gray-400 dark:text-gray-500 font-mono text-[10px] mt-px flex-shrink-0">05</span>
                      <p><span className="text-gray-900 dark:text-white font-medium">{t("uma.dsb.dvm.row5Title")}</span> &mdash; {t("uma.dsb.dvm.row5Desc")}</p>
                    </div>
                  </div>
                </div>

                {/* Learn More */}
                <div className="rounded-[5px] bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 p-3">
                  <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed mb-2">
                    {t("uma.dsb.learnMore")}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <a href="https://docs.polymarket.com/concepts/resolution" target="_blank" rel="noopener noreferrer" className="text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {t("uma.dsb.learnMore.polymarket")}
                    </a>
                    <a href="https://docs.uma.xyz/" target="_blank" rel="noopener noreferrer" className="text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {t("uma.dsb.learnMore.uma")}
                    </a>
                    <a href="https://github.com/UMAprotocol/UMIPs/blob/master/UMIPs/umip-107.md" target="_blank" rel="noopener noreferrer" className="text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline">
                      {t("uma.dsb.learnMore.umip")}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ AI SUMMARY TAB ═══ */}
            {tab === "summary" && dispute?.aiSummary && (
              <div className="relative">
              {authReady && !authenticated && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <button
                    onClick={() => login()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-[5px] text-xs font-bold bg-[#CCFF00] text-gray-900 hover:bg-[#b8e600] transition-colors shadow-lg"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {t("uma.dsb.signIn")}
                  </button>
                </div>
              )}
              <div className={`space-y-4 ${blurClass}`}>
                {/* Warning */}
                <div className="rounded-[5px] bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30 p-3">
                  <p className="text-[11px] text-yellow-800 dark:text-yellow-300 leading-relaxed">
                    {t("uma.dsb.aiWarning")}
                  </p>
                </div>

                {/* Outcome labels: P1=No, P2=Yes, P3=Early Request, P4=Custom */}
                {(() => {
                  const outcomeLabels: Record<string, string> = {
                    P1: t("uma.dsb.ai.p1"),
                    P2: t("uma.dsb.ai.p2"),
                    P3: t("uma.dsb.ai.p3"),
                    P4: t("uma.dsb.ai.p4"),
                    Uncategorized: t("uma.dsb.ai.uncategorized"),
                  };
                  const outcomeColors: Record<string, string> = {
                    P1: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                    P2: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                    P3: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
                    P4: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
                    Uncategorized: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
                  };
                  return Object.entries(dispute.aiSummary!.outcomes).map(([key, val]) => (
                    <div key={key} className="space-y-2">
                      <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-[5px] ${outcomeColors[key] || outcomeColors.Uncategorized}`}>
                        {outcomeLabels[key] || key}
                      </span>
                      <div className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{renderDiscordMarkdown(val.summary)}</div>
                    </div>
                  ));
                })()}

              </div>
              </div>
            )}

            {/* ═══ COMMENTS TAB ═══ */}
            {tab === "comments" && (
              <div className="space-y-4">
                {/* Warning */}
                <div className="rounded-[5px] bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30 p-3">
                  <p className="text-[11px] text-yellow-800 dark:text-yellow-300 leading-relaxed">
                    {t("uma.dsb.commentsWarning")}
                  </p>
                </div>

                {commentsLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex gap-2.5">
                        <div className="w-7 h-7 rounded-[5px] bg-gray-100 dark:bg-[#18181D] animate-pulse flex-shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3 w-24 rounded bg-gray-100 dark:bg-[#18181D] animate-pulse" />
                          <div className="h-10 rounded bg-gray-100 dark:bg-[#18181D] animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">{t("uma.dsb.noComments")}</p>
                ) : (
                  <div className="space-y-4">
                    {comments.map(msg => (
                      <CommentMessage key={msg.id} msg={msg} />
                    ))}
                  </div>
                )}

                {/* Join Discord CTA */}
                <div className="pt-2">
                  <a
                    href="https://discord.com/invite/jsb9XQJ"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full text-xs font-medium px-3 py-2.5 rounded-[5px] bg-[#5865F2]/10 text-[#5865F2] dark:bg-[#5865F2]/20 dark:text-[#7289DA] hover:bg-[#5865F2]/20 dark:hover:bg-[#5865F2]/30 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                    </svg>
                    {t("uma.dsb.joinDiscord")}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** Format comment timestamp using the active locale */
function commentTimeAgo(ts: number, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const now = Date.now() / 1000;
  const diff = now - ts;
  if (diff < 60) return t("uma.dsb.time.justNow");
  if (diff < 3600) return t("uma.dsb.time.mAgo", { n: Math.floor(diff / 60) });
  if (diff < 86400) return t("uma.dsb.time.hAgo", { n: Math.floor(diff / 3600) });
  if (diff < 604800) return t("uma.dsb.time.dAgo", { n: Math.floor(diff / 86400) });
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function CommentMessage({ msg, isReply = false }: { msg: DiscordMessage; isReply?: boolean }) {
  const t = useT();
  return (
    <div className={isReply ? "ml-6 mt-2" : ""}>
      <div className="flex gap-2.5">
        {msg.senderPicture ? (
          <img src={msg.senderPicture} alt="" className="w-7 h-7 rounded-[5px] flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-[5px] bg-gray-200 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
              {msg.sender?.[0]?.toUpperCase() || "?"}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[11px] font-medium text-gray-900 dark:text-white truncate">
              {msg.sender}
            </span>
            <span className="text-[10px] text-gray-400 flex-shrink-0">
              {commentTimeAgo(msg.time, t)}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line break-words">
            {renderDiscordMarkdown(msg.message)}
          </div>
        </div>
      </div>
      {msg.replies && msg.replies.length > 0 && (
        <div className="border-l-2 border-gray-100 dark:border-[#1A1A1A] pl-1">
          {msg.replies.map(reply => (
            <CommentMessage key={reply.id} msg={reply} isReply />
          ))}
        </div>
      )}
    </div>
  );
}
